import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  RefreshCw,
  X,
} from 'lucide-react';
import { usePiercingWorkflow } from '../../contexts/PiercingWorkflowContext';
import PiercingWorkflowStepper from '../PiercingWorkflowStepper';
import { apiClient } from '../../utils/api';
import { useToast } from '../../components/Toast';
import { TemplatePdfPreview } from './TemplatePdfPreview';
import { SignerForm } from './SignerForm';
import {
  IdCardCapture,
  emptyIdCardSides,
  hasAnyIdCardSide,
  hasRequiredIdCardFront,
  type IdCardSides,
} from './IdCardCapture';
import { SignedConfirm } from './SignedConfirm';
import type { CollectedSig, ContractStampSignatures } from './types';
import {
  buildIdCardPhotosPayload,
  isMinorFromBirthDate,
  parseIdCardPhotosFromRaw,
} from './utils';

type ContractStep =
  | 'PRACTITIONER'
  | 'CLIENT'
  | 'GUARDIAN'
  | 'CLIENT_IDENTITY'
  | 'GUARDIAN_IDENTITY'
  | 'REVIEW';

type SignedContractData = {
  signedAt: string;
  actId: string;
};

type ContractRow = {
  status?: string;
  signatures?: unknown;
  signedAt?: string;
  legalGuardian?: unknown;
  idCardPhotos?: unknown;
};

type MobileFooterProps = {
  label: string;
  disabled?: boolean;
  loading?: boolean;
  onClick: () => void | Promise<void>;
};

const MobileFooter: React.FC<MobileFooterProps> = ({
  label,
  disabled = false,
  loading = false,
  onClick,
}) => {
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 border-t border-gray-200 bg-white/95 px-3 py-2 backdrop-blur xl:hidden">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled || loading}
        className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-primary-500 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Enregistrement…
          </>
        ) : (
          <>
            <span className="truncate">{label}</span>
            <ArrowRight className="h-4 w-4 shrink-0" />
          </>
        )}
      </button>
    </div>
  );
};

const PiercingContractScreen: React.FC = () => {
  const navigate = useNavigate();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { appointment, acts, loading, reload } = usePiercingWorkflow();
  const { showToast, ToastComponent } = useToast();

  const isMinor = isMinorFromBirthDate(appointment?.clientBirthDate);

  const contractAct = useMemo(() => {
    if (!acts.length) return null;

    const actWithSignedContract = acts.find((act) =>
      (act.contracts ?? []).some((contract) => contract.status === 'SIGNED'),
    );

    if (actWithSignedContract) return actWithSignedContract;

    const actWithDraftContract = acts.find((act) =>
      (act.contracts ?? []).some((contract) => contract.status === 'DRAFT'),
    );

    if (actWithDraftContract) return actWithDraftContract;

    const actWithAnyContract = acts.find(
      (act) => (act.contracts ?? []).length > 0,
    );

    return actWithAnyContract ?? acts[0];
  }, [acts]);

  const contractPreviewActId = contractAct?.id ?? null;

  const [templateId, setTemplateId] = useState<string | null>(null);
  const [tplLoading, setTplLoading] = useState(true);

  const [practStoredSig, setPractStoredSig] = useState<string | null>(null);
  const [practName, setPractName] = useState('');

  const [sigPract, setSigPract] = useState<CollectedSig | null>(null);
  const [sigClient, setSigClient] = useState<CollectedSig | null>(null);
  const [sigGuardian, setSigGuardian] = useState<CollectedSig | null>(null);

  const [idCardClient, setIdCardClient] = useState<IdCardSides>(emptyIdCardSides);
  const [idCardGuardian, setIdCardGuardian] = useState<IdCardSides>(emptyIdCardSides);

  const [stepIndex, setStepIndex] = useState(0);
  const [clientSignerMountKey, setClientSignerMountKey] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [signedContractData, setSignedContractData] =
    useState<SignedContractData | null>(null);

  const [contractWasDeleted, setContractWasDeleted] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const clientFullName = useMemo(() => {
    if (!appointment) return '';

    return [appointment.clientFirstName, appointment.clientLastName]
      .filter(Boolean)
      .join(' ');
  }, [appointment]);

  const guardianFirstName =
    contractAct?.demandDetails?.guardianFirstName ??
    acts[0]?.demandDetails?.guardianFirstName ??
    '';

  const guardianLastName =
    contractAct?.demandDetails?.guardianLastName ??
    acts[0]?.demandDetails?.guardianLastName ??
    '';

  const guardianFullName =
    [guardianFirstName, guardianLastName].filter(Boolean).join(' ') ||
    'Représentant légal';

  const draftContract = useMemo(() => {
    if (signedContractData || !contractAct) return undefined;

    return (contractAct.contracts ?? []).find(
      (contract) => contract.status === 'DRAFT',
    );
  }, [contractAct, signedContractData]);

  const practSigFromDraft = draftContract?.signatures?.practitioner ?? null;
  const clientSigFromDraft = draftContract?.signatures?.client ?? null;

  const guardianSigFromDraft =
    draftContract?.signatures?.legalGuardian ??
    (
      draftContract as
        | {
            legalGuardian?: {
              dataUrl?: string;
              signerName?: string;
              signedAt?: string;
            };
          }
        | undefined
    )?.legalGuardian ??
    null;

  const isSigned = Boolean(signedContractData);

  const allRequiredSigs = Boolean(
    sigPract && sigClient && (!isMinor || sigGuardian),
  );

  const clientIdentityReady = !isMinor || hasRequiredIdCardFront(idCardClient);

  /**
   * La pièce du représentant n'est actuellement pas techniquement bloquante
   * dans ton ancien code. On garde donc cette règle :
   * le recto client est obligatoire, le représentant reste recommandé.
   */
  const guardianIdentityReady = !isMinor || hasAnyIdCardSide(idCardGuardian);

  const identityReady = clientIdentityReady;

  const requiredSignatureCount = isMinor ? 3 : 2;

  const completedSignatureCount = isSigned
    ? requiredSignatureCount
    : [
        Boolean(sigPract),
        Boolean(sigClient),
        ...(isMinor ? [Boolean(sigGuardian)] : []),
      ].filter(Boolean).length;

  const steps = useMemo<ContractStep[]>(() => {
    const result: ContractStep[] = ['PRACTITIONER', 'CLIENT'];

    if (isMinor) {
      result.push('GUARDIAN', 'CLIENT_IDENTITY', 'GUARDIAN_IDENTITY');
    }

    result.push('REVIEW');

    return result;
  }, [isMinor]);

  const totalSteps = steps.length;
  const currentStep = steps[stepIndex] ?? 'PRACTITIONER';

  const progressPercent = useMemo(() => {
    if (isSigned) return 100;

    return Math.round(
      (Math.min(stepIndex, totalSteps - 1) / Math.max(totalSteps - 1, 1)) * 100,
    );
  }, [isSigned, stepIndex, totalSteps]);

  const serverHasPersistedIdCards = useMemo(() => {
    if (!contractAct) return false;

    const signedContract = (contractAct.contracts ?? []).find(
      (contract) => contract.status === 'SIGNED',
    ) as ContractRow | undefined;

    if (!signedContract?.idCardPhotos) return false;

    const parsed = parseIdCardPhotosFromRaw(signedContract.idCardPhotos);

    return hasAnyIdCardSide(parsed.client) || hasAnyIdCardSide(parsed.guardian);
  }, [contractAct]);

  const stepTitle = useMemo(() => {
    switch (currentStep) {
      case 'PRACTITIONER':
        return 'Signature praticien';

      case 'CLIENT':
        return 'Signature client';

      case 'GUARDIAN':
        return 'Signature représentant';

      case 'CLIENT_IDENTITY':
        return 'Identité du client';

      case 'GUARDIAN_IDENTITY':
        return 'Identité du représentant';

      case 'REVIEW':
        return 'Vérifier le contrat';

      default:
        return 'Contrat';
    }
  }, [currentStep]);

  const stepDescription = useMemo(() => {
    switch (currentStep) {
      case 'PRACTITIONER':
        return 'Signez le contrat en tant que praticien.';

      case 'CLIENT':
        return clientFullName
          ? `${clientFullName}, signez le contrat.`
          : 'Le client doit signer le contrat.';

      case 'GUARDIAN':
        return guardianFullName
          ? `${guardianFullName}, signez comme représentant légal.`
          : 'Le représentant légal doit signer.';

      case 'CLIENT_IDENTITY':
        return 'Photographiez le recto de la pièce du client.';

      case 'GUARDIAN_IDENTITY':
        return 'Ajoutez la pièce du représentant légal si disponible.';

      case 'REVIEW':
        return 'Lisez le contrat puis confirmez votre accord.';

      default:
        return '';
    }
  }, [clientFullName, currentStep, guardianFullName]);

  useEffect(() => {
    if (!contractAct) return;

    const signedContract = (contractAct.contracts ?? []).find(
      (contract) => contract.status === 'SIGNED',
    ) as ContractRow | undefined;

    if (!signedContract?.idCardPhotos) return;

    const parsed = parseIdCardPhotosFromRaw(signedContract.idCardPhotos);

    if (hasAnyIdCardSide(parsed.client)) {
      setIdCardClient(parsed.client);
    }

    if (hasAnyIdCardSide(parsed.guardian)) {
      setIdCardGuardian(parsed.guardian);
    }
  }, [contractAct]);

  useEffect(() => {
    let mounted = true;

    const loadInitialData = async () => {
      setTplLoading(true);

      try {
        const [signatureResponse, templateResponse] = await Promise.all([
          apiClient('/api/practitioner/profile/signature'),
          apiClient(`/api/practitioner/default-contract-template?minor=${isMinor}`),
        ]);

        const signaturePayload = await signatureResponse.json();
        const templatePayload = await templateResponse.json();

        if (!mounted) return;

        if (signaturePayload.success && signaturePayload.data?.signatureData) {
          if (!practSigFromDraft?.dataUrl) {
            setPractStoredSig(signaturePayload.data.signatureData);
          }

          const firstName = signaturePayload.data.firstName ?? '';
          const lastName = signaturePayload.data.lastName ?? '';

          setPractName([firstName, lastName].filter(Boolean).join(' '));
        }

        if (templatePayload.success && templatePayload.data?.template?.id) {
          setTemplateId(templatePayload.data.template.id);
        }
      } catch {
        // Ne bloque jamais le parcours si le template ou la signature stockée échoue.
      } finally {
        if (mounted) {
          setTplLoading(false);
        }
      }
    };

    void loadInitialData();

    return () => {
      mounted = false;
    };
    // L'hydratation des signatures de brouillon est traitée séparément.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMinor]);

  useEffect(() => {
    if (sigPract) return;

    const dataUrl = practSigFromDraft?.dataUrl ?? practStoredSig;

    if (!dataUrl) return;

    const signerName = practSigFromDraft?.signerName ?? practName;

    setSigPract({
      dataUrl,
      signerName,
      signerRole: 'PRACTITIONER',
      signedAt: practSigFromDraft?.signedAt ?? new Date().toISOString(),
    });

    if (practSigFromDraft?.dataUrl) {
      setPractStoredSig(practSigFromDraft.dataUrl);
    }

    if (signerName) {
      setPractName(signerName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practSigFromDraft, practStoredSig]);

  useEffect(() => {
    if (!clientSigFromDraft?.dataUrl || sigClient) return;

    setSigClient({
      dataUrl: clientSigFromDraft.dataUrl,
      signerName: clientSigFromDraft.signerName?.trim() || clientFullName,
      signerRole: 'CLIENT',
      signedAt:
        typeof clientSigFromDraft.signedAt === 'string' &&
        clientSigFromDraft.signedAt
          ? clientSigFromDraft.signedAt
          : new Date().toISOString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientFullName, clientSigFromDraft]);

  useEffect(() => {
    if (!isMinor || !guardianSigFromDraft?.dataUrl || sigGuardian) return;

    setSigGuardian({
      dataUrl: guardianSigFromDraft.dataUrl,
      signerName: guardianSigFromDraft.signerName?.trim() || guardianFullName,
      signerRole: 'LEGAL_GUARDIAN',
      signedAt:
        typeof guardianSigFromDraft.signedAt === 'string' &&
        guardianSigFromDraft.signedAt
          ? guardianSigFromDraft.signedAt
          : new Date().toISOString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guardianFullName, guardianSigFromDraft, isMinor]);

  useEffect(() => {
    if (!acts.length || signedContractData) return;

    const signedAct = acts.find((act) =>
      (act.contracts ?? []).some((contract) => contract.status === 'SIGNED'),
    );

    if (!signedAct) return;

    let mounted = true;

    const loadSignedContract = async () => {
      try {
        const response = await apiClient(
          `/api/practitioner/acts/${signedAct.id}/contract`,
        );

        if (!response.ok) return;

        const payload = await response.json();
        const contract = payload.data as ContractRow | undefined;

        if (!mounted || !contract?.signedAt) return;

        setSignedContractData({
          signedAt: contract.signedAt,
          actId: signedAct.id,
        });

        const parsed = parseIdCardPhotosFromRaw(contract.idCardPhotos);

        if (hasAnyIdCardSide(parsed.client)) {
          setIdCardClient(parsed.client);
        }

        if (hasAnyIdCardSide(parsed.guardian)) {
          setIdCardGuardian(parsed.guardian);
        }
      } catch {
        // L'écran reste disponible même si un ancien contrat ne peut être relu.
      }
    };

    void loadSignedContract();

    return () => {
      mounted = false;
    };
  }, [acts, signedContractData]);

  /**
   * Si le praticien a une signature enregistrée ou si un brouillon existe,
   * on ouvre directement la première étape qui manque.
   */
  useEffect(() => {
    if (isSigned || contractWasDeleted) return;

    const nextIncompleteStep = steps.findIndex((step) => {
      switch (step) {
        case 'PRACTITIONER':
          return !sigPract;

        case 'CLIENT':
          return !sigClient;

        case 'GUARDIAN':
          return !sigGuardian;

        case 'CLIENT_IDENTITY':
          return !clientIdentityReady;

        case 'GUARDIAN_IDENTITY':
          return !guardianIdentityReady;

        case 'REVIEW':
          return false;

        default:
          return false;
      }
    });

    const reviewIndex = steps.indexOf('REVIEW');
    const targetIndex =
      nextIncompleteStep === -1 ? reviewIndex : nextIncompleteStep;

    setStepIndex((previous) =>
      previous === targetIndex ? previous : targetIndex,
    );
  }, [
    clientIdentityReady,
    contractWasDeleted,
    guardianIdentityReady,
    isSigned,
    sigClient,
    sigGuardian,
    sigPract,
    steps,
  ]);

  const persistIdCardsIfNeeded = async (): Promise<boolean> => {
    if (!signedContractData) return true;

    const idCardPhotos = buildIdCardPhotosPayload(idCardClient, idCardGuardian);

    if (!idCardPhotos.length) return true;

    try {
      const response = await apiClient(
        `/api/practitioner/acts/${signedContractData.actId}/contract/id-cards`,
        {
          method: 'PATCH',
          body: JSON.stringify({ idCardPhotos }),
        },
      );

      if (!response.ok) return false;

      await Promise.resolve(reload());

      return true;
    } catch {
      return false;
    }
  };

  const handleRegenerateContract = async () => {
    if (!appointmentId) return;

    setRegenerating(true);

    try {
      const response = await apiClient(
        `/api/practitioner/appointments/${appointmentId}/generate-contract`,
        {
          method: 'POST',
        },
      );

      if (!response.ok) {
        const payload = await response.json();

        throw new Error(
          payload.message || 'Erreur lors de la création du contrat',
        );
      }

      setContractWasDeleted(false);
      setSignedContractData(null);

      setSigPract(null);
      setSigClient(null);
      setSigGuardian(null);

      setIdCardClient(emptyIdCardSides());
      setIdCardGuardian(emptyIdCardSides());

      setClientSignerMountKey((key) => key + 1);
      setStepIndex(0);

      await Promise.resolve(reload());

      showToast('Contrat recréé avec succès', 'success', 3000);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Impossible de régénérer le contrat';

      showToast(message, 'error', 4000);
    } finally {
      setRegenerating(false);
    }
  };

  const handleConfirm = async () => {
    if (!appointmentId || !appointment) return;

    if (!sigPract) {
      setError('La signature du praticien est requise.');
      return;
    }

    if (!sigClient) {
      setError('La signature du client est requise.');
      return;
    }

    if (isMinor && !sigGuardian) {
      setError('La signature du représentant légal est requise.');
      return;
    }

    if (isMinor && !hasRequiredIdCardFront(idCardClient)) {
      setError("Le recto de la pièce d'identité du client est obligatoire pour un mineur.");
      return;
    }

    const mainAct = contractAct ?? acts[0];

    if (!mainAct) {
      setError('Aucun acte trouvé pour ce rendez-vous.');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      const signedAt = new Date().toISOString();

      const idCardPhotos = buildIdCardPhotosPayload(
        idCardClient,
        idCardGuardian,
        signedAt,
      );

      const response = await apiClient(
        `/api/practitioner/acts/${mainAct.id}/contract`,
        {
          method: 'POST',
          body: JSON.stringify({
            type: 'PIERCING_CONSENT',
            practitionerSignatureData: sigPract.dataUrl,
            signatureData: sigClient.dataUrl,
            guardianSignatureData: sigGuardian?.dataUrl ?? null,
            signatures: {
              practitioner: {
                dataUrl: sigPract.dataUrl,
                signerName: sigPract.signerName,
                signedAt: sigPract.signedAt,
              },
              client: {
                dataUrl: sigClient.dataUrl,
                signerName: sigClient.signerName,
                signedAt: sigClient.signedAt,
              },
              ...(sigGuardian
                ? {
                    legalGuardian: {
                      dataUrl: sigGuardian.dataUrl,
                      signerName: sigGuardian.signerName,
                      signedAt: sigGuardian.signedAt,
                    },
                  }
                : {}),
            },
            isMinor,
            signedAt,
            templateId: templateId ?? undefined,
            idCardPhotos: idCardPhotos.length ? idCardPhotos : undefined,
          }),
        },
      );

      if (!response.ok) {
        const payload = await response.json();

        throw new Error(
          payload.message || 'Erreur lors de la sauvegarde du contrat',
        );
      }

      setSignedContractData({
        signedAt,
        actId: mainAct.id,
      });

      await Promise.resolve(reload());

      showToast('Contrat signé et enregistré avec succès', 'success', 3500);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Erreur lors de la sauvegarde du contrat.';

      setError(message);
      showToast(message, 'error', 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinueToCapture = async () => {
    if (!appointmentId) return;

    setContinuing(true);

    try {
      const saved = await persistIdCardsIfNeeded();

      if (!saved) {
        // Ne bloque pas la navigation : la sauvegarde pourra être retentée plus tard.
        showToast(
          "Les pièces d'identité n'ont pas pu être enregistrées, mais vous pouvez continuer.",
          'error',
          4000,
        );
      }

      navigate(`/practitioner/piercing/${appointmentId}/capture`);
    } finally {
      setContinuing(false);
    }
  };

  const handleResetSignedContract = () => {
    setSignedContractData(null);
    setContractWasDeleted(true);

    setSigPract(null);
    setSigClient(null);
    setSigGuardian(null);

    setIdCardClient(emptyIdCardSides());
    setIdCardGuardian(emptyIdCardSides());

    setClientSignerMountKey((key) => key + 1);
    setStepIndex(0);

    void Promise.resolve(reload());
  };

  const goToNextStep = () => {
    setError(null);

    setStepIndex((index) => Math.min(index + 1, totalSteps - 1));
  };

  const goToPreviousStep = () => {
    setError(null);

    setStepIndex((index) => Math.max(index - 1, 0));
  };

  const isCurrentStepReady = useMemo(() => {
    switch (currentStep) {
      case 'PRACTITIONER':
        return Boolean(sigPract);

      case 'CLIENT':
        return Boolean(sigClient);

      case 'GUARDIAN':
        return Boolean(sigGuardian);

      case 'CLIENT_IDENTITY':
        return clientIdentityReady;

      case 'GUARDIAN_IDENTITY':
        /**
         * Non bloquant pour correspondre à la validation précédente :
         * seul le recto client est obligatoire pour les mineurs.
         */
        return true;

      case 'REVIEW':
        return allRequiredSigs && identityReady;

      default:
        return false;
    }
  }, [
    allRequiredSigs,
    clientIdentityReady,
    currentStep,
    identityReady,
    sigClient,
    sigGuardian,
    sigPract,
  ]);

  const signaturesFromSaved = useMemo((): ContractStampSignatures => {
    if (!isSigned || !contractAct) return null;

    const contracts = contractAct.contracts ?? [];

    const contractRow =
      (contracts.find(
        (contract) => contract.status === 'SIGNED',
      ) as ContractRow | undefined) ??
      (contracts.find((contract) => {
        const row = contract as ContractRow;

        if (
          row.legalGuardian &&
          typeof row.legalGuardian === 'object' &&
          'dataUrl' in row.legalGuardian
        ) {
          return true;
        }

        if (!row.signatures || typeof row.signatures !== 'object') {
          return false;
        }

        return Object.values(row.signatures as Record<string, unknown>).some(
          (signature) =>
            Boolean(signature) &&
            signature !== null &&
            typeof signature === 'object' &&
            'dataUrl' in signature &&
            typeof (signature as { dataUrl?: unknown }).dataUrl === 'string',
        );
      }) as ContractRow | undefined);

    if (!contractRow) return null;

    let rawSignatures = contractRow.signatures as Record<string, unknown>;

    if (typeof rawSignatures === 'string') {
      try {
        rawSignatures = JSON.parse(rawSignatures) as Record<string, unknown>;
      } catch {
        rawSignatures = {};
      }
    }

    if (!rawSignatures || typeof rawSignatures !== 'object') {
      rawSignatures = {};
    }

    const fallbackSignedAt = contractRow.signedAt
      ? new Date(contractRow.signedAt).toISOString()
      : new Date().toISOString();

    const mapSignature = (
      value: unknown,
      signerRole: CollectedSig['signerRole'],
    ): CollectedSig | undefined => {
      if (!value || typeof value !== 'object') return undefined;

      const signature = value as {
        dataUrl?: unknown;
        signerName?: unknown;
        signedAt?: unknown;
      };

      if (typeof signature.dataUrl !== 'string' || !signature.dataUrl) {
        return undefined;
      }

      return {
        dataUrl: signature.dataUrl,
        signerName:
          typeof signature.signerName === 'string' ? signature.signerName : '',
        signerRole,
        signedAt:
          typeof signature.signedAt === 'string'
            ? signature.signedAt
            : fallbackSignedAt,
      };
    };

    const practitioner = mapSignature(
      rawSignatures.practitioner,
      'PRACTITIONER',
    );

    const client = mapSignature(rawSignatures.client, 'CLIENT');

    const legalGuardian = mapSignature(
      rawSignatures.legalGuardian ??
        rawSignatures.legal_guardian ??
        contractRow.legalGuardian,
      'LEGAL_GUARDIAN',
    );

    if (!practitioner && !client && !legalGuardian) return null;

    return {
      practitioner,
      client,
      legalGuardian,
    };
  }, [contractAct, isSigned]);

  const stampSignatures = useMemo((): ContractStampSignatures => {
    const result: NonNullable<ContractStampSignatures> = {};

    const practitioner =
      isSigned && signaturesFromSaved?.practitioner?.dataUrl
        ? signaturesFromSaved.practitioner
        : sigPract ?? undefined;

    const client =
      isSigned && signaturesFromSaved?.client?.dataUrl
        ? signaturesFromSaved.client
        : sigClient ?? undefined;

    const legalGuardian =
      isSigned && signaturesFromSaved?.legalGuardian?.dataUrl
        ? signaturesFromSaved.legalGuardian
        : sigGuardian ?? undefined;

    if (practitioner?.dataUrl) {
      result.practitioner = practitioner;
    }

    if (client?.dataUrl) {
      result.client = client;
    }

    if (legalGuardian?.dataUrl) {
      result.legalGuardian = legalGuardian;
    }

    return Object.keys(result).length ? result : null;
  }, [isSigned, sigClient, sigGuardian, sigPract, signaturesFromSaved]);

  const renderDeletedState = () => {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700">
            <X className="h-4 w-4" />
          </div>

          <div>
            <h2 className="text-sm font-bold text-red-900">Contrat supprimé</h2>
            <p className="mt-1 text-xs leading-5 text-red-700">
              Le contrat et les signatures associées ont été supprimés.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleRegenerateContract}
          disabled={regenerating}
          className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 text-sm font-bold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {regenerating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Création…
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4" />
              Recréer le contrat
            </>
          )}
        </button>
      </div>
    );
  };

  const renderSignedState = () => {
    if (!signedContractData) return null;

    return (
      <SignedConfirm
        signedAt={signedContractData.signedAt}
        actId={signedContractData.actId}
        isMinor={isMinor}
        idCardClient={idCardClient}
        idCardGuardian={idCardGuardian}
        onIdCardClient={setIdCardClient}
        onIdCardGuardian={setIdCardGuardian}
        clientName={clientFullName}
        serverHasPersistedIdCards={serverHasPersistedIdCards}
        onAfterIdCardsSaved={() => void Promise.resolve(reload())}
        onReset={handleResetSignedContract}
      />
    );
  };

  const renderWizardStep = () => {
    switch (currentStep) {
      case 'PRACTITIONER':
        return (
          <SignerForm
            key="practitioner-signature"
            title=""
            role="PRACTITIONER"
            defaultName={practName}
            prefillSig={practStoredSig}
            onSign={(signature) => {
              setSigPract(signature);
              setError(null);
              showToast('Signature du praticien confirmée', 'success', 1500);
              goToNextStep();
            }}
            disabled={submitting}
          />
        );

      case 'CLIENT':
        return (
          <SignerForm
            key={`client-signature-${clientSignerMountKey}`}
            title=""
            role="CLIENT"
            defaultName={clientFullName}
            prefillSig={sigClient?.dataUrl ?? clientSigFromDraft?.dataUrl ?? null}
            onSign={(signature) => {
              setSigClient(signature);
              setError(null);
              showToast('Signature du client confirmée', 'success', 1500);
              goToNextStep();
            }}
            onCancelConfirmed={() => {
              setSigClient(null);
              setClientSignerMountKey((key) => key + 1);
            }}
            disabled={submitting}
          />
        );

      case 'GUARDIAN':
        if (!isMinor) return null;

        return (
          <SignerForm
            key="guardian-signature"
            title=""
            role="LEGAL_GUARDIAN"
            defaultName={guardianFullName}
            prefillSig={
              sigGuardian?.dataUrl ?? guardianSigFromDraft?.dataUrl ?? null
            }
            onSign={(signature) => {
              setSigGuardian(signature);
              setError(null);
              showToast(
                'Signature du représentant légal confirmée',
                'success',
                1500,
              );
              goToNextStep();
            }}
            disabled={submitting}
          />
        );

      case 'CLIENT_IDENTITY':
        if (!isMinor) return null;

        return (
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2.5">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
              <p className="text-xs leading-5 text-amber-900">
                Le recto de la pièce d’identité du client est obligatoire.
              </p>
            </div>

            <IdCardCapture
              label={`Pièce d’identité — ${clientFullName}`}
              required
              sides={idCardClient}
              onSidesChange={setIdCardClient}
            />
          </div>
        );

      case 'GUARDIAN_IDENTITY':
        if (!isMinor) return null;

        return (
          <div className="space-y-3">
            <div className="rounded-lg bg-gray-50 px-3 py-2.5">
              <p className="text-xs leading-5 text-gray-600">
                Ajoutez la pièce d’identité du représentant légal si elle est
                disponible. Cette étape peut être passée.
              </p>
            </div>

            <IdCardCapture
              label={`Pièce d’identité — ${guardianFullName}`}
              required={false}
              sides={idCardGuardian}
              onSidesChange={setIdCardGuardian}
            />
          </div>
        );

      case 'REVIEW':
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5">
              <CheckCircle2 className="h-5 w-5 shrink-0 text-green-700" />

              <div className="min-w-0">
                <h3 className="text-sm font-bold text-green-900">
                  Signatures prêtes
                </h3>
                <p className="truncate text-xs text-green-800">
                  Vérifiez le contrat avant de confirmer.
                </p>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                <h3 className="text-sm font-bold text-gray-900">
                  Aperçu du contrat
                </h3>

                <span className="text-[11px] text-gray-400">À vérifier</span>
              </div>

              <div className="h-[calc(100dvh-242px)] min-h-[240px] max-h-[520px]">
                <TemplatePdfPreview
                  templateId={templateId}
                  actId={contractPreviewActId}
                  loading={tplLoading}
                  stampSignatures={stampSignatures}
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading || !appointment) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
          <Loader2 className="h-5 w-5 animate-spin text-primary-500" />
          Chargement…
        </div>
      </div>
    );
  }

  const mobileFooterLabel =
    currentStep === 'REVIEW'
      ? 'J’accepte et je signe'
      : currentStep === 'GUARDIAN_IDENTITY'
        ? 'Passer au contrat'
        : 'Continuer';

  const handleMobileFooterClick = async () => {
    if (currentStep === 'REVIEW') {
      await handleConfirm();
      return;
    }

    goToNextStep();
  };

  return (
    <>
      <ToastComponent />

      <div className="min-h-screen bg-[#f7f8fb] pb-16 xl:hidden">
        <header className="sticky top-0 z-40 h-14 border-b border-gray-200 bg-white/95 px-3 backdrop-blur">
          <div className="flex h-full items-center justify-between gap-2">
            <button
              type="button"
              onClick={() =>
                navigate(`/practitioner/piercing/${appointmentId}/details`)
              }
              disabled={submitting || continuing}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-700 transition hover:bg-gray-200 disabled:opacity-50"
              aria-label="Retour aux actes"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>

            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-gray-900">
                {contractWasDeleted
                  ? 'Contrat supprimé'
                  : isSigned
                    ? 'Contrat signé'
                    : stepTitle}
              </p>

              {!isSigned && !contractWasDeleted && (
                <div className="mt-1 h-1 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-primary-500 transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              )}
            </div>

            {!isSigned && !contractWasDeleted ? (
              <span className="shrink-0 text-xs font-semibold text-gray-500">
                {stepIndex + 1}/{totalSteps}
              </span>
            ) : (
              <span className="w-9 shrink-0" />
            )}
          </div>
        </header>

        <main className="mx-auto flex min-h-[calc(100dvh-56px)] w-full max-w-2xl flex-col px-3 py-3">
          {contractWasDeleted ? (
            <div className="my-auto">{renderDeletedState()}</div>
          ) : isSigned && signedContractData ? (
            <>
              <div className="mb-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
                  <h1 className="text-lg font-bold text-gray-900">
                    Contrat signé et archivé
                  </h1>
                </div>

                <p className="mt-1 text-sm leading-5 text-gray-500">
                  Vous pouvez maintenant passer à la prise des photos.
                </p>
              </div>

              <section className="min-h-0 flex-1">
                {renderSignedState()}
              </section>

              <MobileFooter
                label="Continuer vers les photos"
                loading={continuing}
                onClick={handleContinueToCapture}
              />
            </>
          ) : (
            <>
              <section className="mb-3">
                <p className="text-sm leading-5 text-gray-500">
                  {stepDescription}
                </p>

                {isMinor &&
                  currentStep !== 'CLIENT_IDENTITY' &&
                  currentStep !== 'GUARDIAN_IDENTITY' && (
                    <div className="mt-2 inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-800">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Mineur : identité requise
                    </div>
                  )}
              </section>

              <section className="min-h-0 flex-1">
                <div className="rounded-xl border border-gray-200 bg-white p-2 shadow-sm sm:p-3">
                  {renderWizardStep()}
                </div>

                {error && (
                  <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
              </section>

              <MobileFooter
                label={mobileFooterLabel}
                loading={submitting}
                disabled={!isCurrentStepReady}
                onClick={handleMobileFooterClick}
              />
            </>
          )}
        </main>
      </div>

      {/* =============================================================
          Desktop : actions à gauche, aperçu contrat à droite.
          Visible uniquement à partir de 1280 px.
      ============================================================= */}
      <div className="hidden pb-24 xl:block">
        <PiercingWorkflowStepper currentStep={1} />

        <div className="mb-4 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="truncate text-base font-bold text-gray-900">
                  {isMinor
                    ? 'Contrat de consentement · Mineur'
                    : 'Contrat de consentement'}
                </h1>

                {isMinor && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                    <AlertTriangle className="h-3 w-3" />
                    Identité requise
                  </span>
                )}
              </div>

              <p className="mt-1 text-xs text-gray-500">{clientFullName}</p>
            </div>

            {!isSigned && !contractWasDeleted && (
              <div className="text-right">
                <p className="text-sm font-bold text-gray-900">
                  {completedSignatureCount}/{requiredSignatureCount}
                </p>
                <p className="text-xs text-gray-400">signatures</p>
              </div>
            )}
          </div>

          {!isSigned && !contractWasDeleted && (
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100">
              <div
                className="h-full rounded-full bg-primary-500 transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-[minmax(420px,0.8fr)_minmax(560px,1.2fr)] gap-4">
          <aside className="min-w-0">
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-100 px-5 py-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                  Abraxas
                </p>

                <h2 className="mt-0.5 text-lg font-bold text-gray-900">
                  {contractWasDeleted
                    ? 'Contrat supprimé'
                    : isSigned
                      ? 'Contrat finalisé'
                      : stepTitle}
                </h2>

                {!contractWasDeleted && !isSigned && (
                  <p className="mt-1 text-sm text-gray-500">
                    {stepDescription}
                  </p>
                )}
              </div>

              <div className="p-5">
                {contractWasDeleted ? (
                  renderDeletedState()
                ) : isSigned ? (
                  renderSignedState()
                ) : (
                  <>
                    <div className="mb-5 flex items-center gap-2">
                      {steps.map((step, index) => {
                        const current = index === stepIndex;
                        const completed = index < stepIndex;

                        return (
                          <button
                            key={step}
                            type="button"
                            onClick={() => {
                              if (index <= stepIndex) {
                                setError(null);
                                setStepIndex(index);
                              }
                            }}
                            className={`flex h-8 min-w-8 items-center justify-center rounded-full px-2 text-xs font-bold transition ${
                              current
                                ? 'bg-primary-500 text-white'
                                : completed
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-400'
                            }`}
                            aria-label={`Étape ${index + 1}`}
                          >
                            {completed ? (
                              <CheckCircle2 className="h-4 w-4" />
                            ) : (
                              index + 1
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {renderWizardStep()}

                    {error && (
                      <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>{error}</span>
                      </div>
                    )}

                    <div className="mt-5 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={goToPreviousStep}
                        disabled={stepIndex === 0 || submitting}
                        className="btn-secondary flex h-11 w-11 items-center justify-center px-0 disabled:opacity-50"
                        aria-label="Étape précédente"
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </button>

                      <button
                        type="button"
                        onClick={
                          currentStep === 'REVIEW'
                            ? handleConfirm
                            : goToNextStep
                        }
                        disabled={!isCurrentStepReady || submitting}
                        className="btn-primary flex h-11 flex-1 items-center justify-center gap-2 px-4 disabled:opacity-50"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Enregistrement…
                          </>
                        ) : currentStep === 'REVIEW' ? (
                          <>
                            J’accepte et je signe
                            <ArrowRight className="h-4 w-4" />
                          </>
                        ) : (
                          <>
                            Continuer
                            <ArrowRight className="h-4 w-4" />
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </aside>

          <section className="min-w-0">
            <div className="sticky top-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                    Abraxas
                  </p>
                  <h2 className="mt-0.5 text-base font-bold text-gray-900">
                    Aperçu du contrat
                  </h2>
                </div>

                <span className="text-xs text-gray-400">
                  Mis à jour automatiquement
                </span>
              </div>

              <div className="h-[calc(100vh-180px)] min-h-[620px]">
                <TemplatePdfPreview
                  templateId={templateId}
                  actId={contractPreviewActId}
                  loading={tplLoading}
                  stampSignatures={stampSignatures}
                />
              </div>
            </div>
          </section>
        </div>

        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
            <button
              type="button"
              className="btn-secondary flex h-11 items-center gap-2 px-4"
              onClick={() =>
                navigate(`/practitioner/piercing/${appointmentId}/details`)
              }
              disabled={submitting || continuing}
            >
              <ArrowLeft className="h-4 w-4" />
              Retour aux actes
            </button>

            {isSigned && (
              <button
                type="button"
                className="btn-primary flex h-11 min-w-64 items-center justify-center gap-2 px-5"
                onClick={handleContinueToCapture}
                disabled={continuing}
              >
                {continuing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Enregistrement…
                  </>
                ) : (
                  <>
                    Continuer vers les photos
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PiercingContractScreen;